import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useApp } from '@/contexts/AppContext';

export type CapabilityKey =
  | 'monitoring'
  | 'forecast_basic'
  | 'forecast_extended'
  | 'notifications'
  | 'fleet_insights'
  | 'homeowner_portal'
  | 'issue_queue'
  | 'issue_submission'
  | 'service_history'
  | 'warranty_registry'
  | 'warranty_claim_packets'
  | 'customer_reports'
  | 'upgrade_opportunities_operator'
  | 'upgrade_opportunities_homeowner'
  | 'control_read'
  | 'control_write'
  | 'gateway_management';

const TIER_RANK: Record<string, number> = {
  monitor: 1,
  alert: 2,
  predict: 3,
};

interface UsePropertyCapabilitiesResult {
  capabilities: Set<CapabilityKey>;
  has: (cap: CapabilityKey) => boolean;
  isLoading: boolean;
}

const EMPTY_SET = new Set<CapabilityKey>();
const EMPTY_RESULT: UsePropertyCapabilitiesResult = {
  capabilities: EMPTY_SET,
  has: () => false,
  isLoading: false,
};

/**
 * Resolve capabilities for a property.
 *
 * Operators: full tier capabilities (no plan intersection).
 * Homeowners: intersection of platform tier + assigned service plan.
 * Returns `has()` that defaults to false while loading (safe — never flash gated content).
 */
export function usePropertyCapabilities(
  propertyId: string | undefined,
  plantTiers?: string[],
): UsePropertyCapabilitiesResult {
  const { isInstaller } = useApp();

  // Operator path: get capabilities for highest plant tier
  const highestTier = plantTiers?.length
    ? plantTiers.reduce((best, t) =>
        (TIER_RANK[t] ?? 0) > (TIER_RANK[best] ?? 0) ? t : best
      )
    : null;

  const { data, isLoading } = useQuery({
    queryKey: ['property-capabilities', propertyId, isInstaller, highestTier],
    queryFn: async () => {
      if (!propertyId) return [];

      if (isInstaller && highestTier) {
        // Operator dormancy gate (dunning day-30): a dormant property limits the
        // operator to billing/account views — operational (capability-gated)
        // features go dark. Billing/account routes are not capability-gated, so
        // they remain reachable when capabilities resolve to empty.
        //
        // TODO(dunning Option B): this is a FRONTEND-ONLY gate, not a security
        // boundary — capability enforcement is entirely client-side today. Add real
        // backend enforcement (RPC/RLS) for dormancy (and capabilities generally).
        const { data: dormantRow } = await supabase
          .from('property_service_subscriptions')
          .select('id')
          .eq('property_id', propertyId)
          .not('dormant_at', 'is', null)
          .limit(1)
          .maybeSingle();
        if (dormantRow) return [];

        // Operator: full tier capabilities, no plan intersection
        const { data: caps, error } = await supabase.rpc(
          'get_capabilities_for_tier',
          { p_tier: highestTier }
        );
        if (error) throw error;
        return (caps as CapabilityKey[]) ?? [];
      }

      // Homeowner: dual-gate intersection
      const { data: caps, error } = await supabase.rpc(
        'resolve_property_capabilities',
        { p_property_id: propertyId }
      );
      if (error) throw error;
      return (caps as CapabilityKey[]) ?? [];
    },
    enabled: !!propertyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  if (!propertyId) return EMPTY_RESULT;

  const capSet = data ? new Set(data) : EMPTY_SET;

  return {
    capabilities: capSet,
    has: (cap: CapabilityKey) => capSet.has(cap),
    isLoading,
  };
}
